import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/platform/useProfile";

const Header = () => {
  const [open, setOpen] = useState(false);
  const { userId, data } = useProfile();
  const profile = data?.profile;
  const avatar = userId ? <Avatar className="h-7 w-7 border border-border"><AvatarImage src={profile?.avatar_url ?? undefined} alt="Your profile picture" /><AvatarFallback className="text-xs">{(profile?.full_name || profile?.username || "Y").charAt(0).toUpperCase()}</AvatarFallback></Avatar> : null;

  const close = () => setOpen(false);

  return (
    <header className="fixed top-0 w-full bg-black z-50 border-b border-white/20">
      <div className="container mx-auto px-6 py-3">
        <div className="grid grid-cols-3 items-center">
          {/* Left */}
          <div className="flex items-center gap-2 justify-start">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10 md:hidden"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10 hidden md:flex"
              asChild
            >
              <Link to="/events">Events</Link>
            </Button>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10 hidden md:flex"
              asChild
            >
              <Link to="/pricing">Pricing</Link>
            </Button>

            <Button className="hidden md:flex" asChild>
              <Link to={userId ? "/dashboard" : "/auth"}>{avatar}{userId ? "Dashboard" : "Sign In"}</Link>
            </Button>
          </div>

          {/* Center Logo */}
          <Link
            to="/"
            className="flex flex-col items-center space-y-1 hover:opacity-80 transition-opacity justify-self-center"
          >
            <img
              src="/lovable-uploads/metro-city-groove-logo-white.png"
              alt="Metro City Groove Logo"
              className="w-16 h-16 object-contain drop-shadow-[0_0_12px_rgba(255,255,255,0.25)]"
            />
            <h1 className="text-sm sm:text-lg font-bold text-white text-center whitespace-nowrap">
              Metro City Groove
            </h1>
          </Link>

          {/* Right */}
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10 hidden md:flex"
              asChild
            >
              <Link to="/agency-pricing">Consultation</Link>
            </Button>
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 bg-transparent hidden md:flex"
              asChild
            >
              <Link to="/landing-page-2">Agency Landing</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="md:hidden bg-black border-b border-white/20 animate-fade-in">
          <div className="container mx-auto px-6 py-4 flex flex-col gap-2">
            <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/10" asChild onClick={close}>
              <Link to="/events">Events</Link>
            </Button>
            <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/10" asChild onClick={close}>
              <Link to="/pricing">Pricing</Link>
            </Button>

            <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/10" asChild onClick={close}>
              <Link to="/agency-pricing">Consultation</Link>
            </Button>
            <Button variant="outline" className="w-full justify-start border-white/30 text-white hover:bg-white/10 bg-transparent" asChild onClick={close}>
              <Link to="/landing-page-2">Agency Landing</Link>
            </Button>
            <Button className="w-full" asChild onClick={close}>
              <Link to={userId ? "/dashboard" : "/auth"}>{avatar}{userId ? "Dashboard" : "Sign In"}</Link>
            </Button>
            <Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/10" asChild onClick={close}>
              <Link to="/admin-auth">Agency Sign In</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
